import React, { useState, useEffect, useCallback } from 'react'
import _ from 'lodash/fp'
import { connect } from 'react-redux'
import { graphql } from 'react-apollo'
import classNames from 'classnames'
import { Link } from 'react-router-dom'

import withWidth from '@material-ui/core/withWidth'
import Grid from '@material-ui/core/Grid'
import Select from 'react-select'

import withStyles from '@material-ui/core/styles/withStyles'

import Map from './OffersMap'
import Spinner from '@fe/ui/Spinner'
import Offer from './Offer'

import colors from '@fe/ui/colors'

import apolloClient from '../../apollo'

import { haversineDistance } from '../../util/map'

import { loader } from 'graphql.macro'
const shopify = loader('./shopify.gql')
const collectionQuery = loader('./collection.gql')
const clubsQuery = loader('./clubs.gql')

const getPrice = product => ({
  price: _.flow(_.get('variants.0.priceV2.amount'), Number)(product),
  compareAtPrice: _.flow(_.get('variants.0.compareAtPriceV2.amount'), Number)(product)
})

window.client = apolloClient
window.query = clubsQuery
const normalizeDeep = _.mapValues(v =>
  _.has('edges', v)
    ? _.map(
        _.flow(
          _.get('node'),
          normalizeDeep
        ),
        _.get('edges', v)
      )
    : v)

const styles = theme => ({
  root: {
    minHeight: 'calc(100vh - 64px)',
    backgroundColor: 'white'
  },
  filterBar: {
    display: 'flex',
    padding: 12,
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'flex-start'
  }
})

const offersListStyles = theme => ({
  headerWrapper: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 20%)'
  },
  hover: {
    '&:hover': {
      backgroundColor: colors.paleBlue
    }
  },
  header: {
    textAlign: 'center',
    fontSize: '3em',
    color: colors.lightGreen,
    display: 'block',
    padding: 24,
    gridColumnStart: 2,
    gridColumnEnd: 5,
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.7em'
    }
  },
  description: {
    textAlign: 'center',
    fontSize: '2em',
    color: theme.palette.text.secondary,
    display: 'block',
    padding: 0,
    gridColumnStart: 2,
    gridColumnEnd: 5,
    marginTop: -24,
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.13em'
    }
  },
  backButton: {
    fontSize: '1.2em',
    fontWeight: 'bold',
    color: colors.paleGreen,
    display: 'flex',
    padding: 24,
    gridColumnStart: 1,
    alignItems: 'center',
    justifyContent: 'flex-start',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1em'
    }
  },
  seeAll: {
    fontSize: '1.2em',
    fontWeight: 'bold',
    color: colors.paleGreen,
    display: 'flex',
    padding: 24,
    gridColumnStart: 5,
    alignItems: 'center',
    justifyContent: 'flex-end',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1em'
    }
  },
  container: {
    padding: '0 180px',
    [theme.breakpoints.down('lg')]: {
      padding: 0
    }
  }
})

const OffersList = withStyles(offersListStyles)(({ title, handle, offers, classes, showSeeAll, description }) => {
  const isFeatured = handle === 'featured'
  const gridSizes = isFeatured
    ? { xs: 12 / 3 }
    : { xs: 12 / 2, md: 12 / 3, xl: 12 / 4 }
  return (
    <div>
      {(!isFeatured && (showSeeAll
        ? (
          <Link to={`/offers/${handle}`} className={classNames(classes.headerWrapper, classes.hover)}>
            <div className={classes.header}>{title}</div>
            <div className={classes.seeAll}>More</div>
          </Link>
          )
        : (
          <div className={classes.headerWrapper}>
            <Link to='/offers' className={classes.backButton}>Back</Link>
            <div className={classes.header}>{title}</div>
            {description && <div className={classes.description} dangerouslySetInnerHTML={{ __html: description }} />}
          </div>
          )
      )) || (<div className={classNames(classes.headerWrapper)}><div className={classes.header}>{title}</div></div>)}
      <div className={classes.container}>
        <Grid container>
          {
            _.map(p =>
              <Grid key={p.id} item {...gridSizes} style={{ padding: 12 }}>
                <Offer
                  {...p}
                  image={_.get('images.0.transformedSrc', p)}
                  {...getPrice(p)}
                />
              </Grid>, offers)
          }
        </Grid>
      </div>
    </div>
  )
})

const Offers = ({ data: { loading, error, collections, collectionByHandle, getAllClubs }, classes, handle, width }) => {
  const [position, setPosition] = useState()
  const [zoomLevel, setZoomLevel] = useState(8)
  const [waiting, setWaiting] = useState(true)
  const [mapRef, setMapRef] = useState({ current: null })
  const [clubs, setClubs] = useState({})
  const [waitingForPosition, setWaitingForPosition] = useState(true)
  const updateZoomLevel = useCallback(() => {
    setZoomLevel(_.result('current.getZoom', mapRef))
  }, [mapRef.current])

  const sortOptions = {
    distance: { value: 'distance', label: 'Near me', isDisabled: !position, sortFunc: ({ offer, position }) => _.has('club.position', offer) && position ? haversineDistance(position, _.get('club.position', offer)) : Infinity },
    price: { value: 'price', label: 'Lowest price', sortFunc: _.flow(_.get('offer'), getPrice, _.get('price')) },
    saved: { value: 'saved', label: 'Best offer', sortFunc: ({ offer }) => { const p = getPrice(offer); return p.compareAtPrice ? p.price / p.compareAtPrice : Infinity } },
    newest: { value: 'newest', label: 'Newest', sortFunc: ({ offer }) => +new Date(_.get('createdAt', offer)) },
    oldest: { value: 'oldest', label: 'Oldest', sortFunc: ({ offer }) => -new Date(_.get('createdAt', offer)) }
  }

  const [ordering, setOrdering] = useState(sortOptions.distance)
  useEffect(() => {
    navigator.geolocation.getCurrentPosition((pos) => {
      const p = { latitude: pos.coords.latitude, longitude: pos.coords.longitude }
      setPosition(p)
      setWaitingForPosition(false)
    }, () => {
      setWaitingForPosition(false)
    })
  }, [])

  useEffect(() => {
    setWaiting(true)
    const t = setTimeout(() => setWaiting(false), 1000 + _.random(0, 1000))
    return () => { clearTimeout(t) }
  }, [handle, ordering])

  useEffect(() => {
    const doit = async () => {
      const { data, error } = await apolloClient.query({ query: clubsQuery })
      if (!error) {
        setClubs(
          _.flow(
            _.get('getAllClubs'),
            _.keyBy('id'))(data)
        )
      }
    }
    doit()
  }, [])
  const getClub = id => _.get(id, clubs)

  const data = _.flow(
    _.get('collections'),
    _.reject(_.flow(_.get('products'), _.isEmpty)), // Remove empty collections
    _.map(collection => _.set('products', _.map(p => _.set('club', getClub(_.get('clubId.value', p)), p), _.get('products', collection)), collection)), // Add club to offers
    _.partition({ handle: 'featured' }), // Split collections in two: one with 'featured' collection and one with the rest
    _.spread(_.concat) // join the collections again - now with 'featured' at the start!

  )(
    collectionByHandle
      ? normalizeDeep({ collections: { edges: [{ node: collectionByHandle }] } }) // wrap it for normalization
      : normalizeDeep({ collections })
  )
  const offers = _.flow(
    _.flatMap(_.get('products')),
    _.uniqBy('id')
  )(data)

  const getOffersForCollection = collection => {
    const data = _.flow(
      _.get('products'),
      _.sortBy(offer => ordering.sortFunc({ offer, position }))
    )(collection)

    if (collection.handle === 'featured') {
      return _.take(3, data)
    }
    return handle ? data : _.take(_.get(width, sizeToItemsMap), data)
  }

  return (
    <div className={classes.root}>
      <div style={{ height: '50vh' }}>
        <Map getRef={setMapRef} position={position} offers={waiting ? [] : offers} onZoomChanged={updateZoomLevel} small={zoomLevel < 10} mini={zoomLevel < 7} />
      </div>
      <div className={classes.filterBar}>
        <div>Sort by</div>
        <div style={{ width: 300, paddingLeft: 12 }}>
          <Select
            onChange={setOrdering}
            value={ordering}
            options={_.values(sortOptions)}
          />
        </div>
      </div>
      {
        (waiting || loading || error || waitingForPosition || _.isEmpty(clubs))
          ? <Spinner title='Loading offers' style={{ height: 'calc(50vh - 64px)' }} />
          : _.map(c => <OffersList
              key={c.id}
              offers={getOffersForCollection(c)}
              title={c.title}
              handle={c.handle}
              showSeeAll={!handle}
              description={_.get('descriptionHtml', c)}
                       />, data)
      }
    </div>
  )
}

const sizeToItemsMap = {
  xl: 4,
  lg: 3,
  md: 3,
  sm: 2,
  xs: 2
}

export default _.compose(
  connect(
    (s, ownProps) => {
      return {
        nItems: 250,
        nCollections: 10,
        handle: _.get('match.params.handle', ownProps)
      }
    }
  ),
  graphql(shopify, { skip: _.get('handle') }),
  graphql(collectionQuery, { skip: _.negate(_.get('handle')) }),
  withStyles(styles),
  withWidth()
)(Offers)
